import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Zap, HardHat, Loader2, Lock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

export function ProspectComponents({ prospect, onUpdate }) {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [components, setComponents] = useState({
        easy_power: { enabled: false, stage: null, disabled: false },
        chantier: { enabled: false, stage: null, disabled: false }
    });

    const isAdminFull = (user?.role === 'admin_full' || user?.is_admin === true);
    const isAdmin = (user?.role === 'admin' || user?.is_admin === true);
    const isVP = user?.role === 'vp';
    const canManageComponents = isAdminFull || isAdmin || isVP;

    useEffect(() => {
        if (prospect) {
            loadComponents();
        }
    }, [prospect?.id]);

    const loadComponents = async () => {
        try {
            const res = await api.get(`/prospects/${prospect.id}/components`);
            setComponents(res.data);
        } catch (error) {
            // Fallback sur les données du prospect
            setComponents({
                easy_power: {
                    enabled: !!prospect.easy_power_stage && !prospect.easy_power_disabled,
                    stage: prospect.easy_power_stage,
                    disabled: prospect.easy_power_disabled || false
                },
                chantier: {
                    enabled: !!prospect.chantier_stage && !prospect.chantier_disabled,
                    stage: prospect.chantier_stage,
                    disabled: prospect.chantier_disabled || false
                }
            });
        }
    };

    const handleToggle = async (component, enabled) => {
        // Vérifier les permissions pour la désactivation
        if (!enabled && !canManageComponents) {
            toast.error('Vous n\'avez pas les droits pour désactiver une composante');
            return;
        }

        setLoading(true);
        try {
            const payload = {};
            if (component === 'easy_power') {
                payload.easy_power_enabled = enabled;
            } else {
                payload.chantier_enabled = enabled;
            }

            const res = await api.put(`/prospects/${prospect.id}/components`, payload);
            
            setComponents(res.data.components);
            
            if (res.data.changes && res.data.changes.length > 0) {
                toast.success(res.data.changes.join(', '));
            }
            
            onUpdate?.();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Card className="border-2 border-slate-200" data-testid="prospect-components">
            <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                        <Zap className="w-4 h-4 text-slate-600" />
                    </div>
                    Composantes du dossier
                </CardTitle>
                <CardDescription>
                    Activez ou désactivez les modules Courtage et Travaux d’optimisation pour ce prospect
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Courtage */}
                <div className={cn(
                    "flex items-center justify-between p-4 rounded-lg border-2 transition-all",
                    components.easy_power.enabled 
                        ? "bg-amber-50 border-amber-200" 
                        : components.easy_power.disabled 
                            ? "bg-slate-100 border-slate-200 opacity-60"
                            : "bg-white border-slate-200"
                )}>
                    <div className="flex items-center gap-3">
                        <div className={cn(
                            "w-10 h-10 rounded-lg flex items-center justify-center",
                            components.easy_power.enabled ? "bg-amber-100" : "bg-slate-100"
                        )}>
                            <Zap className={cn(
                                "w-5 h-5",
                                components.easy_power.enabled ? "text-amber-600" : "text-slate-400"
                            )} />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <Label htmlFor="easy-power-toggle" className="font-semibold cursor-pointer">
                                    Courtage
                                </Label>
                                {components.easy_power.enabled && (
                                    <Badge className="bg-amber-100 text-amber-700 text-xs">
                                        {components.easy_power.stage?.replace('ep_', '').replace(/_/g, ' ')}
                                    </Badge>
                                )}
                                {components.easy_power.disabled && (
                                    <Badge variant="outline" className="text-xs border-slate-300 text-slate-500">
                                        <Lock className="w-3 h-3 mr-1" />
                                        Masqué
                                    </Badge>
                                )}
                            </div>
                            <p className="text-xs text-slate-500">
                                Contrats d'électricité professionnelle
                            </p>
                        </div>
                    </div>
                    <Switch
                        id="easy-power-toggle"
                        checked={components.easy_power.enabled}
                        onCheckedChange={(checked) => handleToggle('easy_power', checked)}
                        disabled={loading || prospect.module_permissions?.easy_power?.write === false || (!canManageComponents && components.easy_power.enabled)}
                        data-testid="easy-power-toggle"
                    />
                </div>

                {/* Travaux d’optimisation */}
                <div className={cn(
                    "flex items-center justify-between p-4 rounded-lg border-2 transition-all",
                    components.chantier.enabled 
                        ? "bg-blue-50 border-blue-200" 
                        : components.chantier.disabled 
                            ? "bg-slate-100 border-slate-200 opacity-60"
                            : "bg-white border-slate-200"
                )}>
                    <div className="flex items-center gap-3">
                        <div className={cn(
                            "w-10 h-10 rounded-lg flex items-center justify-center",
                            components.chantier.enabled ? "bg-blue-100" : "bg-slate-100"
                        )}>
                            <HardHat className={cn(
                                "w-5 h-5",
                                components.chantier.enabled ? "text-blue-600" : "text-slate-400"
                            )} />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <Label htmlFor="chantier-toggle" className="font-semibold cursor-pointer">
                                    Travaux d’optimisation
                                </Label>
                                {components.chantier.enabled && (
                                    <Badge className="bg-blue-100 text-blue-700 text-xs">
                                        {components.chantier.stage?.replace('ch_', '').replace(/_/g, ' ')}
                                    </Badge>
                                )}
                                {components.chantier.disabled && (
                                    <Badge variant="outline" className="text-xs border-slate-300 text-slate-500">
                                        <Lock className="w-3 h-3 mr-1" />
                                        Masqué
                                    </Badge>
                                )}
                            </div>
                            <p className="text-xs text-slate-500">
                                Travaux de rénovation énergétique
                            </p>
                        </div>
                    </div>
                    <Switch
                        id="chantier-toggle"
                        checked={components.chantier.enabled}
                        onCheckedChange={(checked) => handleToggle('chantier', checked)}
                        disabled={loading || prospect.module_permissions?.chantier?.write === false || (!canManageComponents && components.chantier.enabled)}
                        data-testid="chantier-toggle"
                    />
                </div>

                {/* Avertissement pour désactivation */}
                {!canManageComponents && (components.easy_power.enabled || components.chantier.enabled) && (
                    <Alert className="bg-slate-50 border-slate-200">
                        <Lock className="h-4 w-4 text-slate-500" />
                        <AlertDescription className="text-slate-600 text-sm">
                            Vous n'avez pas les droits pour désactiver une composante. Contactez votre VP ou un Admin.
                        </AlertDescription>
                    </Alert>
                )}

                {/* Loading indicator */}
                {loading && (
                    <div className="flex items-center justify-center py-2 text-slate-500">
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        <span className="text-sm">Mise à jour...</span>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
